import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  styled,
  Badge,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Agriculture as FarmIcon,
  ShoppingCart as OrderIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import { H6 } from "components/Typography";
import { usePageTitle } from "contexts/PageTitleContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import AppPagination from "components/AppPagination";
import { Stack, Pagination } from "@mui/material";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "4px 12px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
    // backgroundColor: alpha(theme.palette.primary.main, 0.04),
    color: theme.palette.text.primary,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
}));


const SummaryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(30px, -30px)',
  }
}));

const Groceries = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  
  // State management
  const [groceries, setGroceries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredGroceries, setFilteredGroceries] = useState([]);
  
  // Pagination state (backend + fallback client)
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    original_text: '',
    translated_text: '',
    source_language: 'en',
    target_language: ''
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalGroceries: 0,
    activeGroceries: 0,
    totalOrders: 0,
    totalGroceries: 0
  });

  useEffect(() => {
    setPageTitle('Translation Management');
    fetchGroceries(1, 10);
    fetchSupportedLanguages();
  }, [setPageTitle]);

  useEffect(() => {
    // Filter groceries based on search query
    if (!searchQuery.trim()) {
      setFilteredGroceries(groceries);
    } else {
      const filtered = groceries.filter(grocery => 
        grocery.original_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        grocery.translated_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        grocery.source_language?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        grocery.target_language?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredGroceries(filtered);
    }
    // Reset to first page when search changes
    setPage(1);
  }, [searchQuery, groceries]);

  const fetchGroceries = async (pageParam = 1, pageSizeParam = 10) => {
    setLoading(true);
    setError('');
    
    try {
      const url = `${API_ENDPOINTS.GET_groceries}${API_ENDPOINTS.GET_groceries.includes('?') ? '&' : '?'}page=${pageParam}&page_size=${pageSizeParam}`;
      const response = await apiService.get(url);
      
      if (response?.success) {
        const groceriesData = Array.isArray(response.data) ? response.data : [];
        setGroceries(groceriesData);
        setFilteredGroceries(groceriesData);
        
        const paginationData = response?.pagination || response?.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          setPagination({
            current_page: paginationData.current_page ?? pageParam,
            page_size: paginationData.page_size ?? pageSizeParam,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null,
          });
          setCurrentPage(paginationData.current_page ?? pageParam);
          setPageSize(paginationData.page_size ?? pageSizeParam);
          setPage(paginationData.current_page ?? pageParam);
          setRowsPerPage(paginationData.page_size ?? pageSizeParam);
        } else {
          setPagination(prev => ({ ...prev, total_items: groceriesData.length, total_pages: Math.max(1, Math.ceil(groceriesData.length / pageSizeParam)), current_page: 1, page_size: pageSizeParam }));
          setPage(1);
          setRowsPerPage(pageSizeParam);
        }
        
        const stats = {
          totalGroceries: paginationData?.total_items ?? groceriesData.length,
          activeGroceries: groceriesData.length,
          totalOrders: 0,
          totalGroceries: paginationData?.total_items ?? groceriesData.length
        };
        setSummaryStats(stats);
      } else {
        throw new Error(response?.message || 'Failed to fetch groceries');
      }
    } catch (error) {
      console.error('Error fetching groceries:', error);
      setError(error.message || 'Failed to load groceries data');
      toast.showError(error.message || 'Failed to load groceries data');
    } finally {
      setLoading(false);
    }
  };

  const handleGroceriesPageChange = (_, value) => {
    setCurrentPage(value);
    setPage(value);
    fetchGroceries(value, pageSize);
  };

  const handleGroceriesPageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setRowsPerPage(newSize);
    setCurrentPage(1);
    setPage(1);
    fetchGroceries(1, newSize);
  };

  const fetchSupportedLanguages = async () => {
    setLoadingLanguages(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_supported_languages);
      
      if (response?.success) {
        const languagesData = Array.isArray(response.data) ? response.data : [];
        setSupportedLanguages(languagesData);
      } else {
        console.error('Failed to fetch supported languages:', response?.message);
        setSupportedLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching supported languages:', error);
      setSupportedLanguages([]);
    } finally {
      setLoadingLanguages(false);
    }
  };

  const handleRefresh = () => {
    fetchGroceries(currentPage, pageSize);
  };

  // Open dialog for add/edit
  const handleOpenDialog = (grocery = null) => {
    if (grocery) {
      setEditingId(grocery.id);
      setFormData({
        original_text: grocery.original_text || '',
        translated_text: grocery.translated_text || '',
        source_language: grocery.source_language || 'en',
        target_language: grocery.target_language || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        original_text: '',
        translated_text: '',
        source_language: 'en',
        target_language: ''
      });
    }
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData({
      original_text: '',
      translated_text: '',
      source_language: 'en',
      target_language: ''
    });
  };

  // Validate form data
  const validateForm = () => {
    if (!formData.original_text?.trim() || !formData.translated_text?.trim() || !formData.target_language?.trim()) {
      toast.showError('Please fill all required fields (Original Text, Translated Text, and Target Language)');
      return false;
    }
    return true;
  };

  // Add grocery
  const handleAdd = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        original_text: formData.original_text.trim(),
        translated_text: formData.translated_text.trim(),
        target_language: formData.target_language.trim(),
        source_language: formData.source_language?.trim() || null
      };

      const response = await apiService.post(API_ENDPOINTS.Add_grocery, payload);

      if (response?.success) {
        toast.showSuccess('Text translation added successfully');
        handleCloseDialog();
        fetchGroceries(currentPage, pageSize);
      } else {
        toast.showError(response?.message || 'Failed to add text translation');
      }
    } catch (error) {
      console.error('Error adding grocery:', error);
      toast.showError(error?.message || 'Failed to add text translation');
    } finally {
      setSaving(false);
    }
  };

  // Update grocery
  const handleUpdate = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        pk: editingId,
        original_text: formData.original_text.trim(),
        translated_text: formData.translated_text.trim(),
        target_language: formData.target_language.trim(),
        source_language: formData.source_language?.trim() || null
      };

      const response = await apiService.put(
        `${API_ENDPOINTS.Update_grocery}`,
        payload
      );

      if (response?.success) {
        toast.showSuccess('Text translation updated successfully');
        handleCloseDialog();
        fetchGroceries(currentPage, pageSize);
      } else {
        toast.showError(response?.message || 'Failed to update text translation');
      }
    } catch (error) {
      console.error('Error updating grocery:', error);
      toast.showError(error?.message || 'Failed to update text translation');
    } finally {
      setSaving(false);
    }
  };

  // Delete grocery
  const handleDelete = async (grocery) => {
    const confirmed = await showDeleteConfirmation({
      title: 'Delete Text Translation',
      message: `Are you sure you want to delete the translation for "${grocery.original_text}"?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirmed) return;

    setDeletingId(grocery.id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.Delete_grocery}?id=${grocery.id}`
      );

      if (response?.success) {
        toast.showSuccess('Text translation deleted successfully');
        fetchGroceries(currentPage, pageSize);
      } else {
        toast.showError(response?.message || 'Failed to delete translation');
      }
    } catch (error) {
      console.error('Error deleting text:', error);
      toast.showError(error?.message || 'Failed to delete translation');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (text) => {
    if (!text) return 'G';
    return text.charAt(0).toUpperCase();
  };

  // Truncate text - show limited text then "..."
  const truncateText = (text, maxChars = 30) => {
    if (!text) return 'N/A';
    const trimmedText = text.trim();
    
    // Truncate at character limit
    if (trimmedText.length > maxChars) {
      return trimmedText.substring(0, maxChars) + '...';
    }
    
    return trimmedText;
  };

  // Check if text needs truncation
  const needsTruncation = (text, maxChars = 30) => {
    if (!text) return false;
    const trimmedText = text.trim();
    return trimmedText.length > maxChars;
  };

  // Use backend pagination when available; otherwise client-side slice
  const totalItems = pagination.total_items > 0 ? pagination.total_items : filteredGroceries.length;
  const totalPages = pagination.total_pages > 1 ? pagination.total_pages : Math.ceil(filteredGroceries.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedGroceries = pagination.total_items > 0 ? filteredGroceries : filteredGroceries.slice(startIndex, endIndex);
  const displayPage = pagination.total_items > 0 ? currentPage : page;
  const showFrom = pagination.total_items > 0 ? (pagination.current_page - 1) * pagination.page_size + 1 : startIndex + 1;
  const showTo = pagination.total_items > 0 ? Math.min(pagination.current_page * pagination.page_size, pagination.total_items) : Math.min(endIndex, filteredGroceries.length);

  const handlePageChangeLocal = (event, value) => {
    setPage(value);
  };
  const onPageChange = pagination.total_items > 0 ? handleGroceriesPageChange : handlePageChangeLocal;

  if (loading && groceries.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading groceries data...</Typography>
      </Box>
    );
  }

  return (
    <Box pt={2} pb={4}>
      {/* Page Header */}
      <FlexBetween mb={3}>
        <Box>
          <H6 fontSize={18} mb={0.5}>Translation Management</H6>
          {/* <Typography variant="body2" color="text.secondary">
            Manage and view grocery information and statistics
          </Typography> */}
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pageSize} label="Rows" onChange={handleGroceriesPageSizeChange}>
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Add Translation
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </FlexBetween>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} mb={3}> */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <SummaryCard>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5}>
                  {summaryStats.totalGroceries}
                </Typography>
                <Typography variant="body2" opacity={0.8}>
                  Total Groceries
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </FlexBetween>
          </SummaryCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="success.dark">
                  {summaryStats.activeGroceries}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Groceries
                </Typography>
              </Box>
              <Badge 
                badgeContent="Active" 
                color="success"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    minWidth: "50px",
                    height: "20px",
                    padding: "0 8px",
                    top: 8,
                    right: 8,
                  },
                }}
              >
                <PersonIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Badge>
            </FlexBetween>
          </Card>
        </Grid> */}

        {/* <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="info.dark">
                  {summaryStats.totalOrders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </Box>
              <OrderIcon sx={{ fontSize: 40, color: 'info.main' }} />
            </FlexBetween>
          </Card>
        </Grid> */}

        {/* <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="warning.dark">
                  {summaryStats.totalGroceries}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Groceries
                </Typography>
              </Box>
              <FarmIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            </FlexBetween>
          </Card>
        </Grid> */}
      {/* </Grid> */}

      {/* Search and Filters */}
      <StyledCard sx={{ p: 3, mb: 3 }}>
        <FlexBetween mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Translation Directory
          </Typography>
          <Box display="flex" alignItems="center" gap={1} />
        </FlexBetween>

        <TextField
          fullWidth
          placeholder="Search by original text, translated text, or language..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </StyledCard>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Groceries Table */}
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        maxHeight: 500,
        overflow: 'auto',
        padding: "0px 0px 16px 16px",
         /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",

            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            },
      }}
    >
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
     <TableHead
      sx={{
        position: "sticky",
        top: "-20px",
        zIndex: 20,
        backgroundColor: (theme) =>
          theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper,      
      }}
    >
      <TableRow>
        <StyledTableCell className="header" sx={{ width: 300, backgroundColor: (theme) =>
          theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper, }}>
          Original Text
        </StyledTableCell>

        <StyledTableCell className="header" sx={{ width: 300, backgroundColor: (theme) =>
          theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper, }}>
          Translated Text
        </StyledTableCell>

        <StyledTableCell className="header" sx={{ width: 200, backgroundColor: (theme) =>
          theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper, }}>
          Source Language
        </StyledTableCell>

    <StyledTableCell 
      className="header" 
      sx={{ 
        width: 260,
        backgroundColor: (theme) =>
          theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper,      }}
    >
      Translated Text Language
    </StyledTableCell>

    <StyledTableCell
  className="header"
  sx={{
    position: "sticky",
    right: 0,
    top: 0,
    zIndex: 9999, // Very high z-index
    width: 150,
    boxShadow: "-4px 0 6px rgba(0,0,0,0.15) !important",
    backgroundColor: (theme) =>
      theme.palette.mode === "light" ? "rgba(246, 248, 247)" : theme.palette.background.paper,
  }}
>
  Actions
</StyledTableCell>
      </TableRow> 
    </TableHead>


        <TableBody>
          {loading ? (
            <TableRow>
              <StyledTableCell colSpan={5} align="center" sx={{ py: 6 }}>
                <CircularProgress size={32} thickness={4} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading groceries...
                </Typography>
              </StyledTableCell>
            </TableRow>
          ) : filteredGroceries.length === 0 ? (
            <TableRow>
              <StyledTableCell colSpan={5} align="center" sx={{ py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  {searchQuery
                    ? 'No groceries found matching your search'
                    : 'No groceries data available'}
                </Typography>
              </StyledTableCell>
            </TableRow>
          ) : (
            paginatedGroceries.map((grocery) => (
              <TableRow
                key={grocery.id}
                hover
                sx={{
                  transition: 'background 0.2s',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                {/* Original Text */}
                <StyledTableCell sx={{ pl: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: (theme) => theme.palette.primary.main,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#fff',
                      }}
                    >
                      {getInitials(grocery.original_text)}
                    </Avatar> */}
                    <Box>
                      {needsTruncation(grocery.original_text) ? (
                        <Tooltip title={grocery.original_text || 'N/A'} arrow placement="top">
                          <Typography variant="subtitle2" fontWeight={600} sx={{ cursor: 'help' }}>
                            {truncateText(grocery.original_text)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="subtitle2" fontWeight={600}>
                          {grocery.original_text || 'N/A'}
                        </Typography>
                      )}
                      {/* <Typography variant="caption" color="text.secondary">
                        ID: {grocery.id}
                      </Typography> */}
                    </Box>
                  </Box>
                </StyledTableCell>

                {/* Translated Text */}
                <StyledTableCell>
                  {needsTruncation(grocery.translated_text) ? (
                    <Tooltip title={grocery.translated_text || 'N/A'} arrow placement="top">
                      <Typography variant="body2" fontWeight={500} sx={{ cursor: 'help' }}>
                        {truncateText(grocery.translated_text)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>
                      {grocery.translated_text || 'N/A'}
                    </Typography>
                  )}
                </StyledTableCell>

                {/* Source Language */}
                <StyledTableCell>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    >
                    {grocery.source_language_name || 'N/A'}
                  </Typography>
                    {/* size="small"
                    // color={grocery.source_language ? 'primary' : 'default'}
                    variant={grocery.source_language ? 'filled' : 'outlined'} */}
                  
                </StyledTableCell>

                {/* Target Language */}
                <StyledTableCell>
                  {/* <Chip
                    label={grocery.target_language || 'N/A'}
                    size="small"
                    color="success"
                    variant="filled"
                  /> */}
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    >
                    {grocery.target_language_name || 'N/A'}
                  </Typography>
                </StyledTableCell>

                {/* Created At */}
                {/* <StyledTableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(grocery.created_at)}
                  </Typography>
                </StyledTableCell> */}

                {/* Actions */}
                <StyledTableCell 
                sx={{ position: "sticky",
                  right: 0,
                  zIndex: 4,
                  backgroundColor: theme.palette.background.paper, // adapts to dark/light
                  display: "flex", 
                  gap: 1,
                  // minWidth: 150,
                  padding: "25px 20px"}}>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(grocery)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(grocery)}
                        disabled={deletingId === grocery.id}
                      >
                        {deletingId === grocery.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
              </TableRow>
            ))
          )}
        </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination - same style as orders (Showing X - Y of Z + Rows + Pagination) */}
      {filteredGroceries.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {showFrom} - {showTo} of {totalItems} translation{totalItems !== 1? 's' : ''}
          </Typography>
          {totalPages > 1 && (
            <Pagination count={totalPages} page={displayPage} onChange={onPageChange} color="primary" />
          )}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingId ? 'Edit Translation Text' : 'Add Translation Text'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small" disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Original Text "
              fullWidth
              value={formData.original_text}
              onChange={(e) => setFormData({ ...formData, original_text: e.target.value })}
              placeholder="Enter original text (e.g., Apple)"
              required
            />
            <TextField
              label="Translated Text "
              fullWidth
              value={formData.translated_text}
              onChange={(e) => setFormData({ ...formData, translated_text: e.target.value })}
              placeholder="Enter translated text"
              required
            />
            <TextField
              label="Source Language"
              fullWidth
              disabled
              value={formData.source_language}
              onChange={(e) => setFormData({ ...formData, source_language: e.target.value })}
              placeholder="English"
              // helperText="Source language is set to English by default"
            />
            <FormControl fullWidth required>
              <InputLabel>Translated Text Language</InputLabel>
              <Select
                value={formData.target_language}
                onChange={(e) => setFormData({ ...formData, target_language: e.target.value })}
                label="Translated Text Language"
                disabled={loadingLanguages}
              >
                {loadingLanguages ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      Loading languages...
                    </Box>
                  </MenuItem>
                ) : supportedLanguages.length === 0 ? (
                  <MenuItem disabled>No languages available</MenuItem>
                ) : (
                  supportedLanguages.map((language) => (
                    <MenuItem key={language.code || language.language_code || language.id} value={language.code || language.language_code || language.name}>
                      {language.name || language.language_name || language.code || language.language_code}
                    </MenuItem>
                  ))
                )}
              </Select>
              {!loadingLanguages && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                  Select translated text language from the list
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={editingId ? handleUpdate : handleAdd}
            variant="contained"
            disabled={saving || !formData.original_text?.trim() || !formData.translated_text?.trim() || !formData.target_language?.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default Groceries;